import { ClipboardTextIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { AddToReportSubmenuContent } from '@/components';
import { ActionType } from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { usePopoverCloseSignal } from '@/stores';
import { canAddEventToReport } from '@/utils/can-add-event-to-report';
import {
    initialReportEventDrafts,
    type ReportEventCandidate,
} from '@/utils/report-event-draft';
import type { ReportListItem } from '@/types/report';
import { useAddEventsToReport } from './useAddEventToReport';
import type { GridMultiSelectionState } from './useGridMultiSelection';
import { OverlayNames, useOverlayManager } from './useOverlayManager';

export const MAX_EDITABLE_EVENTS = 200;

interface AddEventsToReportActionArgs {
    events: ReportEventCandidate[];
    selection: Pick<
        GridMultiSelectionState,
        'selectedIds' | 'exitMultiSelectMode'
    >;
    query?: string | null;
}

export function useAddEventsToReportAction({
    events,
    selection,
    query,
}: AddEventsToReportActionArgs): ActionConfig {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const closePopover = usePopoverCloseSignal((state) => state.trigger);
    const { open } = useOverlayManager();
    const { busy, addEventsToReport } = useAddEventsToReport();

    const selectedEvents = events.filter(
        (candidate) =>
            selection.selectedIds.has(candidate.event.id) &&
            canAddEventToReport(candidate.event),
    );
    const ready = !busy && selectedEvents.length > 0;

    const addSelectedToReport = async (report: ReportListItem) => {
        const drafts = initialReportEventDrafts({
            candidates: selectedEvents,
            t,
            query,
        });
        if (drafts.length > MAX_EDITABLE_EVENTS) {
            const result = await addEventsToReport({ report, drafts });
            if (result.added > 0) {
                selection.exitMultiSelectMode();
            }
            return;
        }
        open(OverlayNames.ADD_EVENTS_TO_REPORT, {
            report,
            drafts,
            onAdded: selection.exitMultiSelectMode,
        });
    };

    return {
        type: ActionType.ITEM,
        icon: ClipboardTextIcon,
        label: t('actions.addToReport'),
        disabled: !ready,
        content: ready ? (
            <AddToReportSubmenuContent
                onSelect={(report) => void addSelectedToReport(report)}
                onClose={closePopover}
            />
        ) : undefined,
    };
}
